import { getDesignerDpi, pxToMm } from './editorUnits';

export type BackgroundBreakpoint = {
  /** Design width threshold in millimeters (inclusive). */
  up: number;
  url: string;
  x: number;
  y: number;
};

const cache: Record<string, BackgroundBreakpoint[]> = {};

export async function loadBackgroundBreakpoints(optionIdentifier: string): Promise<BackgroundBreakpoint[]> {
  if (cache[optionIdentifier]) {
    return cache[optionIdentifier];
  }

  try {
    const response = await fetch(`/background_images/${optionIdentifier}/breakpoints.json`);
    if (!response.ok) {
      cache[optionIdentifier] = [];
      return [];
    }

    const data = (await response.json()) as BackgroundBreakpoint[];
    if (!Array.isArray(data)) {
      cache[optionIdentifier] = [];
      return [];
    }

    const sorted = [...data].sort((a, b) => a.up - b.up);
    cache[optionIdentifier] = sorted;
    return sorted;
  } catch {
    cache[optionIdentifier] = [];
    return [];
  }
}

export function pickBreakpoint(
  breakpoints: BackgroundBreakpoint[],
  designWidthMm: number
): BackgroundBreakpoint | null {
  if (!breakpoints.length) return null;
  const match = breakpoints.find((bp) => designWidthMm <= bp.up);
  return match || breakpoints[breakpoints.length - 1];
}

export function buildBackgroundImageUrl(optionIdentifier: string, breakpoint: BackgroundBreakpoint): string {
  return `/background_images/${optionIdentifier}/${breakpoint.url}`;
}

export function computeBackgroundOffset(
  breakpoint: BackgroundBreakpoint,
  imageWidthPx: number,
  imageHeightPx: number,
  dpi = getDesignerDpi()
): { x: number; y: number; width: number; height: number } {
  const width = pxToMm(imageWidthPx, dpi);
  const height = pxToMm(imageHeightPx, dpi);
  const centerX = pxToMm(breakpoint.x, dpi);
  const centerY = pxToMm(breakpoint.y, dpi);

  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width,
    height,
  };
}
